// StoplossClaimsDashboard.tsx
//
// Revamp (Feature #6161): server-side pre-aggregated analytics + tabbed UI with
// drill-through KPI cards, modeled on the Policy Dashboard. Replaces the old
// dashboard's three 10,000-row list fetches + in-browser reduce aggregation.
// All monetary figures are sourced from total_paid_amount (final_cumulative_total
// is vestigial and deliberately not surfaced).
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Grid2 as Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { useGetList } from "react-admin";
import {
  Block as DeniedIcon,
  HourglassTop as CycleIcon,
  Paid as PaidIcon,
  PendingActions as PendedIcon,
  ReceiptLong as ClaimsIcon,
} from "@mui/icons-material";

import ClaimsByAverageDaysToProcessChart from "@/components/charts/claimCharts/ClaimsByAverageDaysToProcess";
import ClaimsByMonthLineChart from "@/components/charts/claimCharts/ClaimsByMonthLineChart";
import ClaimsByStatusChart from "@/components/charts/claimCharts/ClaimsByStatusChart";
import DiagnosisAreaChart from "@/components/charts/claimCharts/DiagnosisAreaChart";
import DiagnosisChapterChart from "@/components/charts/claimCharts/DiagnosisChapterChart";
import SubmissionByClientChart from "@/components/charts/claimCharts/SubmissionByClientChart";
import LaseredExceedanceChart from "@/components/charts/claimCharts/LaseredExceedanceChart";
import CarrierReimbursementBumpChart from "@/components/charts/claimCharts/CarrierReimbursementBumpChart";
import { DashboardCard } from "@/components/common/DashboardCard";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { MetricCard, MetricCardsGrid } from "@/components/common/MetricCards";
import { PageContent, TabContent } from "@/components/common/PageLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { StyledTab, StyledTabs } from "@/components/ui/mui/StyledTabs";
import { type Policy, ResourceName, ViewByClaimType } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { StoplossClaimsDashboardFilters } from "./StoplossClaimsDashboardFilters";
import { buildClaimDrillLink } from "./buildClaimDrillLink";
import { useStoplossClaimsDashboardData } from "./useStoplossClaimsDashboardData";

export const StoplossClaimsDashboard = () => {
  const [searchParams] = useSearchParams();
  // The global count/amount toggle was retired; distribution charts show claim
  // volume, and dollar figures live in the KPIs, cost/aging views, and tooltips.
  const viewBy = ViewByClaimType.CLAIM_COUNT;
  const [activeTab, setActiveTab] = useState(0);

  const rawFilter = searchParams.get("filter");
  // Memoize on rawFilter so `filters` keeps a stable identity across renders
  // (otherwise the drill-link useMemos and the analytics query key recompute).
  const filters = useMemo<Record<string, string | string[]>>(() => {
    try {
      return rawFilter ? JSON.parse(rawFilter) : {};
    } catch {
      return {};
    }
  }, [rawFilter]);

  // Lightweight policy list (capped) — used only to populate the filter dropdowns.
  const { data: allPolicies = [] } = useGetList<Policy>(ResourceName.POLICIES, {
    pagination: { page: 1, perPage: 1000 },
  });

  const {
    data: analytics,
    isLoading,
    isError,
    error,
  } = useStoplossClaimsDashboardData({
    client_id_ref: filters.client_id_ref,
    vendor_id_ref: filters.vendor_id_ref,
    date_from: typeof filters.date_from === "string" ? filters.date_from : undefined,
    date_to: typeof filters.date_to === "string" ? filters.date_to : undefined,
  });

  const metrics = analytics?.metrics;
  const overview = analytics?.overview;
  const costDrivers = analytics?.cost_drivers;
  const totalClaims = metrics?.total_claim_count ?? 0;

  // Tab definitions with a data-presence check. Empty tabs are hidden once data
  // has loaded (matches PolicyDashboard tab-gating). Render blocks key off the
  // tab's stable `key`, not its position, so hiding a tab doesn't misroute.
  type TabKey = "overview" | "cycle" | "cost" | "lasered" | "threshold";
  const tabDefs = useMemo<{ key: TabKey; label: string; hasData: boolean }[]>(
    () => [
      { key: "overview", label: "Overview", hasData: totalClaims > 0 },
      {
        key: "cycle",
        label: "Cycle Time & Aging",
        hasData:
          (analytics?.cycle_time.time_to_reimbursement_buckets.length ?? 0) > 0 ||
          (analytics?.cycle_time.open_aging_buckets.length ?? 0) > 0 ||
          (analytics?.cycle_time.carrier_reimbursement_ranks.length ?? 0) > 0,
      },
      {
        key: "cost",
        label: "Cost Drivers",
        hasData:
          (costDrivers?.high_cost_claimants.length ?? 0) > 0 ||
          (costDrivers?.diagnosis_distribution.length ?? 0) > 0 ||
          (costDrivers?.diagnosis_by_chapter.length ?? 0) > 0 ||
          (costDrivers?.med_rx_line_spend.length ?? 0) > 0,
      },
      {
        key: "lasered",
        label: "Lasered Claimants",
        hasData:
          (analytics?.lasered.lasered_claimants.length ?? 0) > 0 ||
          (analytics?.lasered.exceedance_trend.length ?? 0) > 0,
      },
      {
        key: "threshold",
        label: "Threshold Crossings",
        hasData: (analytics?.threshold_crossings.recent.length ?? 0) > 0,
      },
    ],
    [analytics, costDrivers, totalClaims]
  );
  // While loading, show all tabs; once loaded, only those with data.
  const visibleTabs = isLoading ? tabDefs : tabDefs.filter((t) => t.hasData);
  const activeKey: TabKey = visibleTabs[activeTab]?.key ?? "overview";
  // Lasered and threshold data can exist for a partner with zero stoploss_claims,
  // so the "nothing to show" gate keys off ANY section having data — not just the
  // stoploss claim count — otherwise those tabs would be wrongly suppressed.
  const hasAnyData = tabDefs.some((t) => t.hasData);

  // If the active index falls outside the visible set (data changed / tab hidden), reset.
  useEffect(() => {
    if (!isLoading && activeTab >= visibleTabs.length && visibleTabs.length > 0) setActiveTab(0);
  }, [isLoading, activeTab, visibleTabs.length]);

  // Chart-shaped adapters for the reused claim charts. status_distribution already
  // arrives in business-lifecycle order from the backend, so the donut slices and
  // legend follow that order.
  const statusChartData = useMemo(
    () =>
      (overview?.status_distribution ?? []).map((s) => ({
        name: s.status,
        value: { claims: s.claim_count, amount: s.total_paid },
      })),
    [overview]
  );
  const monthlyChartData = useMemo(
    () => (overview?.monthly_volume ?? []).map((m) => ({ name: m.month, claims: m.claim_count, amount: m.total_paid })),
    [overview]
  );
  const typeChartData = useMemo(
    () =>
      (overview?.type_distribution ?? []).map((t) => ({
        name: t.claim_type,
        value: { claims: t.claim_count, amount: t.total_paid },
      })),
    [overview]
  );
  // Med vs Rx by underlying line-item paid spend (claim_items), distinct from
  // Overview's split which is by the stop-loss claim's own type flag.
  const medRxChartData = useMemo(
    () =>
      (costDrivers?.med_rx_line_spend ?? []).map((m) => ({
        name: m.claim_type,
        value: { claims: m.item_count, amount: m.total_paid },
      })),
    [costDrivers]
  );
  // Bar-chart shape reused from ClaimsByAverageDaysToProcess: {name, claims, amount}.
  const timeToReimbData = useMemo(
    () =>
      (analytics?.cycle_time.time_to_reimbursement_buckets ?? []).map((b) => ({
        name: b.bucket,
        claims: b.claim_count,
        amount: 0,
      })),
    [analytics]
  );
  const openAgingData = useMemo(
    () =>
      (analytics?.cycle_time.open_aging_buckets ?? []).map((b) => ({
        name: b.bucket,
        claims: b.claim_count,
        amount: b.total_paid,
      })),
    [analytics]
  );

  // Drill-throughs: each KPI card lands on the claims list filtered to exactly
  // the population its metric was computed over (carrying the current filters).
  const openLink = useMemo(() => buildClaimDrillLink(filters, { claim_status: ["new", "in_process"] }), [filters]);
  const pendedLink = useMemo(() => buildClaimDrillLink(filters, { claim_status: "pended" }), [filters]);
  const deniedLink = useMemo(() => buildClaimDrillLink(filters, { claim_status: "denied" }), [filters]);
  const reimbursedLink = useMemo(
    () => buildClaimDrillLink(filters, { claim_status: ["reimbursed", "partially_reimbursed"] }),
    [filters]
  );
  const allClaimsLink = useMemo(() => buildClaimDrillLink(filters), [filters]);

  const handleTabChange = (_e: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);

  return (
    <>
      <PageHeader
        customTitle="Stop-Loss Claims Dashboard"
        customSubtitle="Where claims stand and what needs attention — pended, denied, high-cost, and time-to-reimbursement."
        hideSectionTitle
        headerIcon={<ClaimsIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />

      <PageContent>
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to load dashboard data. {error instanceof Error ? error.message : "Please try again."}
          </Alert>
        )}

        {!isLoading && !isError && !hasAnyData && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 12,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            <ClaimsIcon sx={{ fontSize: 48, mb: 2, opacity: 0.4 }} />
            <Typography variant="h6" fontWeight={500}>
              No claims yet
            </Typography>
            <Typography variant="body2">
              Claims data will appear here once stop-loss claims exist for this partnership.
            </Typography>
          </Box>
        )}

        {(isLoading || hasAnyData) && (
          <>
            <Box sx={{ pb: 4 }}>
              <MetricCardsGrid columns={5}>
                <MetricCard
                  icon={<ClaimsIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Open Claims"
                  value={(metrics?.open_claim_count ?? 0).toLocaleString()}
                  subtitle="New + in process"
                  link={openLink}
                  hint="Claims not yet submitted — the active work queue"
                  isLoading={isLoading}
                />
                <MetricCard
                  icon={<PendedIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Pended Claims"
                  value={(metrics?.pended_claim_count ?? 0).toLocaleString()}
                  subtitle={formatUSD(metrics?.pended_amount ?? 0)}
                  link={pendedLink}
                  hint="Claims pended — blocked, awaiting action"
                  isLoading={isLoading}
                />
                <MetricCard
                  icon={<CycleIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Avg Days to Reimbursement"
                  value={Math.round(metrics?.avg_days_to_reimbursement ?? 0).toLocaleString()}
                  subtitle="Submission → payment"
                  link={reimbursedLink}
                  hint="Average calendar days from claim submission to reimbursement payment. Opens reimbursed claims."
                  isLoading={isLoading}
                />
                <MetricCard
                  icon={<DeniedIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Denied Claims"
                  value={(metrics?.denied_claim_count ?? 0).toLocaleString()}
                  subtitle={formatUSD(metrics?.denied_amount ?? 0)}
                  link={deniedLink}
                  hint="Claims denied by the carrier"
                  isLoading={isLoading}
                />
                <MetricCard
                  icon={<PaidIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Total Paid"
                  value={formatUSD(metrics?.total_paid ?? 0)}
                  subtitle={`${(metrics?.total_claim_count ?? 0).toLocaleString()} claims · ${(metrics?.unique_claimant_count ?? 0).toLocaleString()} claimants`}
                  link={allClaimsLink}
                  hint="Total paid across all claims (total_paid_amount)"
                  isLoading={isLoading}
                />
              </MetricCardsGrid>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <StyledTabs value={activeTab} onChange={handleTabChange}>
                {visibleTabs.map((t) => (
                  <StyledTab key={t.key} label={t.label} />
                ))}
              </StyledTabs>
              <StoplossClaimsDashboardFilters allPolicies={allPolicies} filters={filters as Record<string, string[]>} />
            </Box>

            {activeKey === "overview" && (
              <TabContent>
                <Grid container spacing={1.5}>
                  <Grid size={12}>
                    <DashboardCard
                      title="Claims by Incurred Month"
                      description="Monthly claim volume and paid amount by first service date."
                    >
                      {(isFullscreen) => (
                        <ClaimsByMonthLineChart data={monthlyChartData} viewBy={viewBy} isFullscreen={isFullscreen} />
                      )}
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Claims by Status"
                      description="Distribution across all claim statuses, in lifecycle order."
                    >
                      {(isFullscreen) => (
                        <ClaimsByStatusChart data={statusChartData} viewBy={viewBy} isFullscreen={isFullscreen} />
                      )}
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Medical vs Rx (stop-loss claims)"
                      description="Split by the stop-loss claim's own type flag."
                    >
                      {(isFullscreen) => (
                        <ClaimsByStatusChart data={typeChartData} viewBy={viewBy} isFullscreen={isFullscreen} />
                      )}
                    </DashboardCard>
                  </Grid>
                  <Grid size={12}>
                    <DashboardCard
                      title="Aura Submission by Client (top 15)"
                      description="Claims submitted vs not submitted to Aura, by client — top 15 by claim volume."
                    >
                      {(isFullscreen) => (
                        <SubmissionByClientChart
                          data={overview?.submission_by_client ?? []}
                          isFullscreen={isFullscreen}
                        />
                      )}
                    </DashboardCard>
                  </Grid>
                </Grid>
              </TabContent>
            )}

            {/* Cost Drivers — underlying MEDICAL claim lines (claim_items), not the
                stop-loss layer. Money here is claim_items.paid_amount and will not
                reconcile to the KPI "Total Paid" (stop-loss total_paid_amount). */}
            {activeKey === "cost" && (
              <TabContent>
                <Grid container spacing={1.5}>
                  <Grid size={12}>
                    <DashboardCard
                      title="Highest-Cost Claimants (medical spend)"
                      description="Ranked by underlying medical/Rx paid (claim_items) — not the stop-loss reimbursement level."
                    >
                      <HighCostClaimantsTable rows={costDrivers?.high_cost_claimants ?? []} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Top ICD-10 Codes by Avg Paid"
                      description="Average paid per claim line by ICD-10 code, ranked. Coded ICD is sparse — lines without a code are excluded."
                    >
                      {(isFullscreen) => (
                        <DiagnosisAreaChart
                          data={costDrivers?.diagnosis_distribution ?? []}
                          isFullscreen={isFullscreen}
                        />
                      )}
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Medical vs Rx (line spend)"
                      description="Split by underlying claim_items paid — true utilization split."
                    >
                      {(isFullscreen) => (
                        <ClaimsByStatusChart data={medRxChartData} viewBy={viewBy} isFullscreen={isFullscreen} />
                      )}
                    </DashboardCard>
                  </Grid>
                  <Grid size={12}>
                    <DashboardCard
                      title="Spend by Clinical Category (ICD-10 Chapter)"
                      description="Medical spend grouped into ICD-10-CM chapters, derived natively from the code — the coarse 'which body system is driving cost' view. Top 12 by total paid."
                    >
                      {(isFullscreen) => (
                        <DiagnosisChapterChart
                          data={costDrivers?.diagnosis_by_chapter ?? []}
                          isFullscreen={isFullscreen}
                        />
                      )}
                    </DashboardCard>
                  </Grid>
                </Grid>
              </TabContent>
            )}

            {activeKey === "lasered" && (
              <TabContent>
                <Grid container spacing={1.5}>
                  <Grid size={12}>
                    <DashboardCard
                      title="Lasered Claimants"
                      description="Straight and contract lasers with spend vs the effective deductible (per claimant-policy)."
                    >
                      <LaseredTable rows={analytics?.lasered.lasered_claimants ?? []} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={12}>
                    <DashboardCard
                      title="Deductible Breaches by Month & Level"
                      description="Each bubble is a month × effective-deductible level; its size is how many lasered claimants are over that deductible. Same authoritative source as the table above, so the counts reconcile."
                    >
                      {(isFullscreen) => (
                        <LaseredExceedanceChart
                          data={analytics?.lasered.exceedance_trend ?? []}
                          isFullscreen={isFullscreen}
                        />
                      )}
                    </DashboardCard>
                  </Grid>
                </Grid>
              </TabContent>
            )}

            {activeKey === "cycle" && (
              <TabContent>
                <Grid container spacing={1.5}>
                  <Grid size={6}>
                    <DashboardCard
                      title="Time to Reimbursement"
                      description="Days from claim submission to reimbursement payment."
                    >
                      {(isFullscreen) => (
                        <ClaimsByAverageDaysToProcessChart
                          data={timeToReimbData}
                          viewBy={ViewByClaimType.CLAIM_COUNT}
                          isFullscreen={isFullscreen}
                        />
                      )}
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Open & Pended Claim Aging"
                      description="Unresolved claims (new / in process / pended) by days open."
                    >
                      {(isFullscreen) => (
                        <ClaimsByAverageDaysToProcessChart
                          data={openAgingData}
                          viewBy={viewBy}
                          isFullscreen={isFullscreen}
                        />
                      )}
                    </DashboardCard>
                  </Grid>
                  <Grid size={12}>
                    <DashboardCard
                      title="Carrier Reimbursement Speed (rank by month)"
                      description="Each line is a carrier; y-axis is their avg days-to-reimburse rank that month (1 = fastest, top). Lines bump as carriers overtake each other."
                    >
                      {(isFullscreen) => (
                        <CarrierReimbursementBumpChart
                          data={analytics?.cycle_time.carrier_reimbursement_ranks ?? []}
                          isFullscreen={isFullscreen}
                        />
                      )}
                    </DashboardCard>
                  </Grid>
                </Grid>
              </TabContent>
            )}

            {activeKey === "threshold" && (
              <TabContent>
                <Grid container spacing={1.5}>
                  <Grid size={12}>
                    <DashboardCard
                      title="Recent Threshold Crossings"
                      description="Claimants approaching or breaching their deductible — the early-warning ledger."
                    >
                      <ThresholdTable rows={analytics?.threshold_crossings.recent ?? []} />
                    </DashboardCard>
                  </Grid>
                </Grid>
              </TabContent>
            )}
          </>
        )}
      </PageContent>
    </>
  );
};

const HighCostClaimantsTable = ({
  rows,
}: {
  rows: {
    claimant_id: string;
    claimant_name: string;
    client_name: string;
    carrier_name: string;
    item_count: number;
    total_paid: number;
    medical_paid: number;
    rx_paid: number;
    top_diagnosis: string;
  }[];
}) => {
  if (!rows.length) return <EmptyChartState />;
  return (
    <Box sx={{ overflowX: "auto", maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Claimant</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Carrier</TableCell>
            <TableCell>Primary Dx</TableCell>
            <TableCell align="right">Medical</TableCell>
            <TableCell align="right">Rx</TableCell>
            <TableCell align="right">Total Paid</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.claimant_id} hover>
              <TableCell>
                <Link to={`/claimants/${r.claimant_id}`}>{r.claimant_name}</Link>
              </TableCell>
              <TableCell>{r.client_name}</TableCell>
              <TableCell>{r.carrier_name}</TableCell>
              <TableCell>{r.top_diagnosis}</TableCell>
              <TableCell align="right">{formatUSD(r.medical_paid)}</TableCell>
              <TableCell align="right">{formatUSD(r.rx_paid)}</TableCell>
              <TableCell align="right">{formatUSD(r.total_paid)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const LaseredTable = ({
  rows,
}: {
  rows: {
    claimant_id: string;
    claimant_name: string;
    client_name: string;
    carrier_name: string;
    laser_type: string;
    is_contract_lasered: boolean;
    amt_lasered_deductible: number;
    spend_over_deductible: number;
    total_paid: number;
  }[];
}) => {
  if (!rows.length) return <EmptyChartState />;
  return (
    <Box sx={{ overflowX: "auto", maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Claimant</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Carrier</TableCell>
            <TableCell>Laser</TableCell>
            <TableCell align="right">Effective Deductible</TableCell>
            <TableCell align="right">Spend Over Ded.</TableCell>
            <TableCell align="right">Total Paid</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={`${r.claimant_id}-${i}`} hover>
              <TableCell>
                <Link to={`/claimants/${r.claimant_id}`}>{r.claimant_name}</Link>
              </TableCell>
              <TableCell>{r.client_name}</TableCell>
              <TableCell>{r.carrier_name}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={r.is_contract_lasered ? "Contract" : r.laser_type || "Laser"}
                  color={r.is_contract_lasered ? "secondary" : "default"}
                />
              </TableCell>
              <TableCell align="right">{formatUSD(r.amt_lasered_deductible)}</TableCell>
              <TableCell align="right">{formatUSD(r.spend_over_deductible)}</TableCell>
              <TableCell align="right">{formatUSD(r.total_paid)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const ThresholdTable = ({
  rows,
}: {
  rows: {
    claimant_id: string;
    claimant_name: string;
    client_name: string;
    carrier_name: string;
    threshold_percentage: number;
    ytd_amount_at_crossing: number;
    deductible_amount: number;
    deductible_type: string;
    crossing_detected_date: string;
  }[];
}) => {
  if (!rows.length) return <EmptyChartState />;
  return (
    <Box sx={{ overflowX: "auto", maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Claimant</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Carrier</TableCell>
            <TableCell align="right">Threshold</TableCell>
            <TableCell align="right">YTD at Crossing</TableCell>
            <TableCell align="right">Deductible</TableCell>
            <TableCell>Detected</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={`${r.claimant_id}-${r.crossing_detected_date}-${i}`} hover>
              <TableCell>
                <Link to={`/claimants/${r.claimant_id}`}>{r.claimant_name}</Link>
              </TableCell>
              <TableCell>{r.client_name}</TableCell>
              <TableCell>{r.carrier_name}</TableCell>
              <TableCell align="right">{r.threshold_percentage}%</TableCell>
              <TableCell align="right">{formatUSD(r.ytd_amount_at_crossing)}</TableCell>
              <TableCell align="right">{formatUSD(r.deductible_amount)}</TableCell>
              <TableCell>{r.crossing_detected_date}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
